import type { OnboardingStep, OnboardingStepId } from "./types";

// Literal readonly tuple so `z.enum` (and other consumers) can infer the exact
// union at the call site. The `satisfies` clause guarantees the tuple members
// stay in sync with `OnboardingStepId`.
export const ONBOARDING_STEP_IDS = [
  "platform-orientation",
  "client-intake",
  "integration-brand-book",
  "migration",
  "build-creative",
  "testing-approvals",
  "go-live",
] as const satisfies readonly OnboardingStepId[];

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: "platform-orientation",
    stepNumber: 1,
    title: "Platform Orientation",
    description:
      "Pitch materials and competitive positioning for brand conversations.",
    totalSubSteps: 2,
    completedSubSteps: 0,
    subSteps: [
      {
        title: "Platform Orientation",
        description: "Understand what Connect does and how it works",
        stepsCount: 2,
        estimatedTime: "~15m",
        type: "checklist",
      },
      {
        title: "Client Intake",
        description:
          "Gather everything you need from the brand before starting setup",
        stepsCount: 3,
        estimatedTime: "~20m",
        extraInfo:
          "From client: Brand assets, Shopify access, DNS access, text messaging registration",
        type: "checklist",
      },
      {
        title: "Integration & Brand Book",
        description:
          "Connect the store, set up email sending, and configure the brand",
        stepsCount: 4,
        estimatedTime: "~47m",
        extraInfo: "From client: Subdomain preference, brand assets",
        type: "checklist",
      },
      {
        title: "Migration",
        description:
          "Move the brand's audience data into Connect and prepare for launch",
        stepsCount: 4,
        estimatedTime: "~30m",
        extraInfo:
          "From client: Subscriber export, suppression lists, ESP credentials",
        type: "checklist",
      },
      {
        title: "Build Creative",
        description: "Learn the editor and build your first tiles",
        stepsCount: 2,
        estimatedTime: "~25m",
        type: "checklist",
      },
      {
        title: "Testing & Approvals",
        description: "QA your setup and get client sign-off",
        stepsCount: 2,
        estimatedTime: "~15m",
        extraInfo: "From client: Final sign-off",
        type: "checklist",
      },
      {
        title: "Go Live & Prove Value",
        description: "Launch, monitor, and demonstrate results to the brand",
        stepsCount: 3,
        estimatedTime: "~20m",
        type: "checklist",
      },
      {
        title: "See a fully onboarded brand",
        description:
          "Walk through a live brand on Connect to see the end state — this is what you're building toward",
        stepsCount: 0,
        estimatedTime: "~10 min",
        type: "video",
        videoPlaceholder: true,
      },
    ],
  },
  {
    id: "client-intake",
    stepNumber: 2,
    title: "Client Intake",
    description:
      "Gather everything you need from the brand before starting setup.",
    totalSubSteps: 3,
    completedSubSteps: 0,
    subSteps: [],
  },
  {
    id: "integration-brand-book",
    stepNumber: 3,
    title: "Integration & Brand Book",
    description:
      "Connect the store, set up email sending, and configure the brand",
    totalSubSteps: 4,
    completedSubSteps: 0,
    subSteps: [],
  },
  {
    id: "migration",
    stepNumber: 4,
    title: "Migration",
    description:
      "Move the brand's audience data into Connect and prepare for launch",
    totalSubSteps: 1,
    completedSubSteps: 0,
    subSteps: [],
  },
  {
    id: "build-creative",
    stepNumber: 5,
    title: "Build Creative",
    description: "Learn the editor and build your first tiles",
    totalSubSteps: 2,
    completedSubSteps: 0,
    subSteps: [
      {
        title: "Learn the email editor",
        description:
          "How to use the editor, customize tiles, and layer in custom creative",
        stepsCount: 0,
        estimatedTime: "~15m",
        type: "video",
        videoUploadedId: "nbg1LJoSrxc",
        videoPlaceholder: true,
      },
      {
        title: "Learn how to build broadcasts",
        description:
          "How to create, schedule, and send email and text broadcasts",
        stepsCount: 0,
        estimatedTime: "~10m",
        type: "video",
        videoUploadedId: "93Zz1vA8iL4",
        videoPlaceholder: true,
      },
    ],
  },
  {
    id: "testing-approvals",
    stepNumber: 6,
    title: "Testing & Approvals",
    description: "QA your setup and get client sign-off",
    totalSubSteps: 2,
    completedSubSteps: 0,
    subSteps: [
      {
        title: "QA and client walkthrough",
        description:
          "How to QA your setup and walk the brand through Connect to get sign-off",
        stepsCount: 0,
        estimatedTime: "~15 min",
        type: "video",
        videoPlaceholder: true,
      },
      {
        title: "Confirm QA and client approval",
        description:
          "I've completed QA and the brand has signed off on creative, logic, and launch plan",
        stepsCount: 0,
        estimatedTime: "",
        type: "confirmation",
      },
    ],
  },
  {
    id: "go-live",
    stepNumber: 7,
    title: "Go Live & Prove Value",
    description: "Launch, monitor, and demonstrate results to the brand",
    totalSubSteps: 3,
    completedSubSteps: 0,
    subSteps: [
      {
        title: "What to expect after going live",
        description:
          "Cutover planning, monitoring initial sends, and what to watch for",
        stepsCount: 0,
        estimatedTime: "~10 min",
        type: "video",
        videoPlaceholder: true,
      },
      {
        title: "Reporting and client performance",
        description:
          "How to use Connect reporting and talk to clients about performance",
        stepsCount: 0,
        estimatedTime: "~10 min",
        type: "video",
        videoPlaceholder: true,
      },
      {
        title: "Confirm brand is live",
        description:
          "The brand is live on Connect, sending emails, and the previous ESP has been deactivated",
        stepsCount: 0,
        estimatedTime: "",
        type: "confirmation",
      },
    ],
  },
];
